var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Fragment, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { DataContext } from '../player/DataContext';
import globalStore from '../store';
import { ColorEnum } from '../components/ColorPicker';
import { v4 as uuidv4 } from 'uuid';
import { saveState } from '../helper/stateManagerHelper';
import { playAudio } from '../helper/audioHelper';
import { linkSelectedCubes, unlinkSelectedCubes, CUBE_SIZE, CSS_CUBE_SIZE, detectSnapTarget, } from './LinkingCubes';
import '../styles/widgets/linkingCubes.scss';
import ButtonComponent from '../components/ButtonComponent';
import CustomScrollbar from '../components/CustomScrollbar';
import { announce } from '../helper/announcer';
import { getVisibleMidpoint } from '../helper/viewportHelper';
import { reorderChainByIndex } from '../helper/helper';
import { t } from 'i18next';
var CUBE_COLORS = [
    'RED',
    'BLUE',
    'BROWN',
    'GREEN',
    'PURPLE',
    'YELLOW',
    'ORANGE',
    'BLACK',
    'WHITE',
    'GRAY',
];
var CUBE_COLOR_INDEX = {
    RED: 0,
    BLUE: 1,
    BROWN: 2,
    GREEN: 3,
    PURPLE: 4,
    YELLOW: 5,
    ORANGE: 6,
    BLACK: 7,
    WHITE: 8,
    GRAY: 9,
    DARK_NAVY_BLUE: 10,
};
// Canonical screen-reader-friendly color name per ColorEnum key (uses i18n).
// Avoid raw `colorKey.toLowerCase()` which reads "dark_navy_blue" or mis-reads
// abbreviations to SR users.
var getColorName = function (colorKey) {
    var _a;
    var keyMap = {
        RED: 'text.colorRed',
        BLUE: 'text.colorBlue',
        BROWN: 'text.colorBrown',
        ORANGE: 'text.colorOrange',
        GREEN: 'text.colorGreen',
        PURPLE: 'text.colorPurple',
        YELLOW: 'text.colorYellow',
        BLACK: 'text.colorBlack',
        WHITE: 'text.colorWhite',
        GRAY: 'text.colorGray',
        DARK_NAVY_BLUE: 'text.colorDarkNavyBlue',
    };
    return t((_a = keyMap[colorKey]) !== null && _a !== void 0 ? _a : 'text.cube');
};
var getCubeImgSrc = function (orientation, colorKey) { return ({
    normal: "./assets/images/manipulative/linkingCube/".concat(orientation, "/cubeN_").concat(CUBE_COLOR_INDEX[colorKey], ".svg"),
    hover: "./assets/images/manipulative/linkingCube/".concat(orientation, "/cubeH_").concat(CUBE_COLOR_INDEX[colorKey], ".svg"),
}); };
var LinkingCubesToolbar = function () {
    var _a = useContext(DataContext), selectedTool = _a.selectedTool, setSelectedTool = _a.setSelectedTool, manipulative = _a.manipulative, setManipulative = _a.setManipulative;
    var zoomLevel = globalStore.useStoreState(function (store) { return store.playerStore; }).zoomLevel;
    var btnConfig = globalStore.useStoreState(function (st) { return st.jsonDataStore; }).btnConfig;
    var link = btnConfig.link, unLink = btnConfig.unLink;
    var _b = useState(null), countMode = _b[0], setCountMode = _b[1];
    var _c = useState(null), dragState = _c[0], setDragState = _c[1];
    var dragRef = useRef(null);
    var manipulativeRef = useRef(manipulative);
    // Keep refs in sync with state for event handlers
    useEffect(function () {
        dragRef.current = dragState;
    }, [dragState]);
    useEffect(function () {
        manipulativeRef.current = manipulative;
    }, [manipulative]);
    // Ref to hold the temporary touchmove blocker so the useEffect can clean it up
    var touchScrollBlockerRef = useRef(null);
    // Given a desired drop center, walk outward in a square-spiral of CUBE_SIZE
    // steps until the proposed cluster's bounding box doesn't overlap any
    // existing cube. Used when keyboard/VoiceOver drops a cube at the canvas
    // scroll-center — without this, repeated drops stack at the same point.
    // A `buffer` margin is inflated around existing cubes so keyboard placements
    // don't end up adjacent to (and thus visually "linked to") them.
    var PLACEMENT_BUFFER = CUBE_SIZE; // one cube-width gap on every side
    var findNonOverlappingCenter = function (desiredCenter, orientation, count, manipulativeList) {
        var existing = manipulativeList
            .filter(function (m) { return m.data.type === 'linkingCubes'; })
            .map(function (m) { return m.data; });
        if (existing.length === 0)
            return desiredCenter;
        var clusterBounds = function (cx, cy) {
            return orientation === 'horizontal'
                ? {
                    left: cx - CUBE_SIZE / 2,
                    right: cx + (count - 0.5) * CUBE_SIZE,
                    top: cy - CUBE_SIZE / 2,
                    bottom: cy + CUBE_SIZE / 2,
                }
                : {
                    left: cx - CUBE_SIZE / 2,
                    right: cx + CUBE_SIZE / 2,
                    top: cy - (count - 0.5) * CUBE_SIZE,
                    bottom: cy + CUBE_SIZE / 2,
                };
        };
        var overlaps = function (cx, cy) {
            var nb = clusterBounds(cx, cy);
            return existing.some(function (c) {
                var _a;
                var s = CUBE_SIZE * ((_a = c.scale) !== null && _a !== void 0 ? _a : 1);
                // Inflate each existing cube's bounding box by PLACEMENT_BUFFER so a
                // new cluster has to land clearly outside the snap zone.
                var cLeft = c.center.x - s / 2 - PLACEMENT_BUFFER;
                var cRight = c.center.x + s / 2 + PLACEMENT_BUFFER;
                var cTop = c.center.y - s / 2 - PLACEMENT_BUFFER;
                var cBottom = c.center.y + s / 2 + PLACEMENT_BUFFER;
                return nb.left < cRight && nb.right > cLeft && nb.top < cBottom && nb.bottom > cTop;
            });
        };
        if (!overlaps(desiredCenter.x, desiredCenter.y))
            return desiredCenter;
        for (var ring = 1; ring <= 40; ring++) {
            var rim = [];
            for (var dy = -ring; dy <= ring; dy++)
                rim.push({ dx: ring, dy: dy });
            for (var dx = ring - 1; dx >= -ring; dx--)
                rim.push({ dx: dx, dy: ring });
            for (var dy = ring - 1; dy >= -ring; dy--)
                rim.push({ dx: -ring, dy: dy });
            for (var dx = -ring + 1; dx <= ring - 1; dx++)
                rim.push({ dx: dx, dy: -ring });
            for (var _i = 0, rim_1 = rim; _i < rim_1.length; _i++) {
                var _a = rim_1[_i], dx = _a.dx, dy = _a.dy;
                var cx = desiredCenter.x + dx * CUBE_SIZE;
                var cy = desiredCenter.y + dy * CUBE_SIZE;
                if (!overlaps(cx, cy))
                    return { x: cx, y: cy };
            }
        }
        return desiredCenter;
    };
    // Keyboard / click placement: drop a cube (or group of `countMode`) at the
    // current scroll center of the whiteboard canvas. Used by Enter/Space key
    // (native click on button) and VoiceOver double-tap. Mirrors the mouse-drop
    // path but with a synthesized "drop at canvas center" position.
    var placeCubeAtCanvas = function (colorKey, orientation) {
        var _a;
        var canvas = document.querySelector('.whiteboardScrollbar .customScrollbarContent');
        if (!canvas)
            return;
        var rect = canvas.getBoundingClientRect();
        var z = zoomLevel || 1;
        var canvasX = (canvas.scrollLeft + rect.width / 2) / z;
        var canvasY = (canvas.scrollTop + rect.height / 2) / z;
        var count = countMode || 1;
        var dropCenter = findNonOverlappingCenter({ x: canvasX, y: canvasY }, orientation, count, manipulativeRef.current);
        // Keyboard / VoiceOver placements should NEVER auto-link. Unlike mouse
        // drag-drop (where dropping near an existing cube is an explicit snap
        // gesture), here the user has no precise control over where the cube
        // lands, so auto-snapping is surprising. The picker-driven "Link to Cube"
        // context-menu action is the sanctioned path for keyboard linking.
        // Dead-typed to match detectSnapTarget's return so the existing snap
        // branch below still type-checks without triggering a `never` narrowing.
        var snap = null;
        // Build cube objects (mirrors handleMouseUp placement logic)
        var cubes = [];
        var chainId;
        var baseChainIndex = 0;
        var cubeScale = 1;
        var startCenter = dropCenter;
        var targetUpdateId;
        if (snap) {
            playAudio('cubeLink');
            var targetCube = snap.targetCube, snappedPosition = snap.snappedPosition, side = snap.side;
            cubeScale = (_a = targetCube.scale) !== null && _a !== void 0 ? _a : 1;
            startCenter = snappedPosition;
            if (targetCube.chainId) {
                chainId = targetCube.chainId;
                if (side === 'before') {
                    baseChainIndex = 0;
                }
                else {
                    var chainCubes = manipulativeRef.current.filter(function (m) { return m.data.type === 'linkingCubes' && m.data.chainId === chainId; });
                    baseChainIndex = chainCubes.length;
                }
            }
            else {
                chainId = "chain_".concat(uuidv4().replace(/-/g, ''));
                targetUpdateId = targetCube.id;
                baseChainIndex = side === 'before' ? 0 : 1;
            }
        }
        else if (count > 1) {
            chainId = "chain_".concat(uuidv4().replace(/-/g, ''));
        }
        var scaledSize = CUBE_SIZE * cubeScale;
        var snapSide = snap === null || snap === void 0 ? void 0 : snap.side;
        var adjustedStart = __assign({}, startCenter);
        if (snapSide === 'before' && count > 1) {
            if (orientation === 'horizontal')
                adjustedStart.x -= (count - 1) * scaledSize;
            else
                adjustedStart.y += (count - 1) * scaledSize;
        }
        for (var i = 0; i < count; i++) {
            var cubeId = "linkingCube_".concat(uuidv4().replace(/-/g, ''));
            var center = orientation === 'horizontal'
                ? { x: adjustedStart.x + i * scaledSize, y: adjustedStart.y }
                : { x: adjustedStart.x, y: adjustedStart.y - i * scaledSize };
            cubes.push({
                type: 'linkingCubes',
                data: __assign({ id: cubeId, type: 'linkingCubes', center: center, position: center, color: ColorEnum[colorKey], orientation: orientation, scale: cubeScale, rotation: 0, angle: 0, lock: { positioning: false, editing: false } }, (chainId ? { chainId: chainId, chainIndex: baseChainIndex + i } : {})),
            });
        }
        // Save undo entry
        if (cubes.length > 1) {
            saveState({
                type: 'multipleManipulatives',
                id: cubes[0].data.id,
                propertyName: 'batchCreate',
                old: JSON.stringify(cubes),
            });
        }
        else if (cubes.length === 1) {
            saveState({
                type: cubes[0].data.id,
                id: cubes[0].data.id.split('_')[1],
                propertyName: 'create',
                old: JSON.stringify(cubes[0]),
            });
        }
        var insertCount = cubes.length;
        setManipulative(function (prev) {
            var updated = prev;
            if (targetUpdateId && chainId) {
                var targetIdx_1 = snapSide === 'before' ? insertCount : 0;
                updated = prev.map(function (m) {
                    return m.data.id === targetUpdateId
                        ? __assign(__assign({}, m), { data: __assign(__assign({}, m.data), { chainId: chainId, chainIndex: targetIdx_1 }) }) : m;
                });
            }
            else if (snapSide === 'before' && chainId) {
                updated = prev.map(function (m) {
                    var _a;
                    return m.data.type === 'linkingCubes' && m.data.chainId === chainId
                        ? __assign(__assign({}, m), { data: __assign(__assign({}, m.data), { chainIndex: ((_a = m.data.chainIndex) !== null && _a !== void 0 ? _a : 0) + insertCount }) }) : m;
                });
            }
            var allItems = __spreadArray(__spreadArray([], updated, true), cubes, true);
            if (chainId) {
                // Reorder ascending by chainIndex so array position matches the
                // intended stacking (highest chainIndex = frontmost).
                return reorderChainByIndex(allItems, chainId);
            }
            return allItems;
        });
        var newCubeIds = cubes.map(function (c) { return c.data.id; });
        setSelectedTool(newCubeIds);
        // Move focus to the first new cube on canvas for keyboard users
        setTimeout(function () {
            var el = document.querySelector("[data-manipulative-id=\"".concat(newCubeIds[0], "\"], [data-id=\"").concat(newCubeIds[0], "\"]"));
            el === null || el === void 0 ? void 0 : el.focus();
        }, 100);
    };
    var handleCubeDragStart = function (colorKey, orientation) {
        return function (e) {
            e.preventDefault();
            e.stopPropagation();
            // React registers onTouchStart as passive, so e.preventDefault() above
            // silently fails on touch. Register a non-passive touchmove blocker
            // immediately to prevent the scrollable container from scrolling.
            if ('touches' in e) {
                var blocker = function (ev) { return ev.preventDefault(); };
                touchScrollBlockerRef.current = blocker;
                window.addEventListener('touchmove', blocker, { passive: false });
            }
            var clientX = 'clientX' in e ? e.clientX : e.touches[0].clientX;
            var clientY = 'clientY' in e ? e.clientY : e.touches[0].clientY;
            var newDrag = {
                active: true,
                color: ColorEnum[colorKey],
                colorKey: colorKey,
                orientation: orientation,
                count: countMode || 1,
                pos: { x: clientX, y: clientY },
            };
            setDragState(newDrag);
        };
    };
    // Global mouse handlers for drag
    useEffect(function () {
        if (!(dragState === null || dragState === void 0 ? void 0 : dragState.active))
            return;
        // Remove the temporary touchmove blocker (the useEffect's handler takes over)
        if (touchScrollBlockerRef.current) {
            window.removeEventListener('touchmove', touchScrollBlockerRef.current);
            touchScrollBlockerRef.current = null;
        }
        var handleMouseMove = function (e) {
            // Prevent container scroll during touch drag (matches FractionTile pattern)
            if ('touches' in e)
                e.preventDefault();
            var clientX = 'clientX' in e ? e.clientX : e.touches[0].clientX;
            var clientY = 'clientX' in e ? e.clientY : e.touches[0].clientY;
            setDragState(function (prev) { return (prev ? __assign(__assign({}, prev), { pos: { x: clientX, y: clientY } }) : null); });
        };
        var handleMouseUp = function (e) {
            var _a;
            var currentDrag = dragRef.current;
            if (!currentDrag)
                return;
            var clientX = 'clientX' in e ? e.clientX : e.changedTouches[0].clientX;
            var clientY = 'clientX' in e ? e.clientY : e.changedTouches[0].clientY;
            // Convert screen coordinates to canvas coordinates
            var canvas = document.querySelector('.whiteboardScrollbar .customScrollbarContent');
            if (!canvas) {
                setDragState(null);
                return;
            }
            var rect = canvas.getBoundingClientRect();
            var z = zoomLevel || 1;
            var canvasX = (clientX - rect.left + canvas.scrollLeft) / z;
            var canvasY = (clientY - rect.top + canvas.scrollTop) / z;
            // Don't create if dropped outside canvas
            if (clientX < rect.left ||
                clientX > rect.right ||
                clientY < rect.top ||
                clientY > rect.bottom) {
                setDragState(null);
                return;
            }
            // Don't create if dropped on the linking cubes toolbar or right side panel
            var isInsideEl = function (el) {
                if (!el)
                    return false;
                var r = el.getBoundingClientRect();
                return clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom;
            };
            if (isInsideEl(document.querySelector('.linkingCubesToolbar')) ||
                isInsideEl(document.querySelector('#rightSideDrawerContainer')) ||
                isInsideEl(document.querySelector('#rightSideToolPanel')) ||
                isInsideEl(document.querySelector('.headerContainer')) ||
                isInsideEl(document.querySelector('.zoomAndPanControlPanel'))) {
                setDragState(null);
                return;
            }
            // ── Snap detection: check if drop position is near an existing cube ──
            var dropCenter = { x: canvasX, y: canvasY };
            var snap = detectSnapTarget(dropCenter, currentDrag.orientation, [], manipulativeRef.current);
            // Create cube(s)
            var cubes = [];
            var chainId;
            var baseChainIndex = 0;
            var cubeScale = 1;
            var startCenter = dropCenter;
            var targetUpdateId; // standalone target that needs chainId
            if (snap) {
                playAudio('cubeLink');
                var targetCube = snap.targetCube, snappedPosition = snap.snappedPosition, side = snap.side;
                cubeScale = (_a = targetCube.scale) !== null && _a !== void 0 ? _a : 1;
                startCenter = snappedPosition;
                if (targetCube.chainId) {
                    chainId = targetCube.chainId;
                    if (side === 'before') {
                        // Inserting before chain start — new cubes get indices 0..count-1
                        baseChainIndex = 0;
                    }
                    else {
                        // Appending after chain end
                        var chainCubes = manipulativeRef.current.filter(function (m) { return m.data.type === 'linkingCubes' && m.data.chainId === chainId; });
                        baseChainIndex = chainCubes.length;
                    }
                }
                else {
                    // Target is standalone — create new chain for both
                    chainId = "chain_".concat(uuidv4().replace(/-/g, ''));
                    targetUpdateId = targetCube.id;
                    if (side === 'before') {
                        // New cubes at 0..count-1, target shifts to count
                        baseChainIndex = 0;
                    }
                    else {
                        baseChainIndex = 1; // target becomes index 0, new cubes start at 1
                    }
                }
            }
            else if (currentDrag.count > 1) {
                // No snap but group mode — create a new chain for the group
                chainId = "chain_".concat(uuidv4().replace(/-/g, ''));
            }
            var scaledSize = CUBE_SIZE * cubeScale;
            var snapSide = snap === null || snap === void 0 ? void 0 : snap.side;
            // For 'before' snap, adjust startCenter to the far end so cubes
            // iterate toward the target — keeps chainIndex aligned with spatial order.
            var adjustedStart = __assign({}, startCenter);
            if (snapSide === 'before' && currentDrag.count > 1) {
                if (currentDrag.orientation === 'horizontal') {
                    adjustedStart.x -= (currentDrag.count - 1) * scaledSize;
                }
                else {
                    adjustedStart.y += (currentDrag.count - 1) * scaledSize;
                }
            }
            for (var i = 0; i < currentDrag.count; i++) {
                var cubeId = "linkingCube_".concat(uuidv4().replace(/-/g, ''));
                var center = currentDrag.orientation === 'horizontal'
                    ? { x: adjustedStart.x + i * scaledSize, y: adjustedStart.y }
                    : { x: adjustedStart.x, y: adjustedStart.y - i * scaledSize };
                var newCube = {
                    type: 'linkingCubes',
                    data: __assign({ id: cubeId, type: 'linkingCubes', center: center, position: center, color: currentDrag.color, orientation: currentDrag.orientation, scale: cubeScale, rotation: 0, angle: 0, lock: { positioning: false, editing: false } }, (chainId ? { chainId: chainId, chainIndex: baseChainIndex + i } : {})),
                };
                cubes.push(newCube);
            }
            // Save undo/redo: single grouped entry if multiple cubes, individual if one
            if (cubes.length > 1) {
                saveState({
                    type: 'multipleManipulatives',
                    id: cubes[0].data.id,
                    propertyName: 'batchCreate',
                    old: JSON.stringify(cubes),
                });
            }
            else if (cubes.length === 1) {
                saveState({
                    type: cubes[0].data.id,
                    id: cubes[0].data.id.split('_')[1],
                    propertyName: 'create',
                    old: JSON.stringify(cubes[0]),
                });
            }
            // Single atomic update: update standalone target + add new cubes
            var insertCount = cubes.length;
            setManipulative(function (prev) {
                var updated = prev;
                if (targetUpdateId && chainId) {
                    // Assign chainId/chainIndex to the standalone target cube
                    var targetIdx_2 = snapSide === 'before' ? insertCount : 0;
                    updated = prev.map(function (m) {
                        if (m.data.id === targetUpdateId) {
                            return __assign(__assign({}, m), { data: __assign(__assign({}, m.data), { chainId: chainId, chainIndex: targetIdx_2 }) });
                        }
                        return m;
                    });
                }
                else if (snapSide === 'before' && chainId) {
                    // Shift existing chain members up by insertCount
                    updated = prev.map(function (m) {
                        var _a;
                        if (m.data.type === 'linkingCubes' && m.data.chainId === chainId) {
                            return __assign(__assign({}, m), { data: __assign(__assign({}, m.data), { chainIndex: ((_a = m.data.chainIndex) !== null && _a !== void 0 ? _a : 0) + insertCount }) });
                        }
                        return m;
                    });
                }
                var allItems = __spreadArray(__spreadArray([], updated, true), cubes, true);
                // Reorder ascending by chainIndex so array position matches the
                // intended stacking (highest chainIndex = frontmost).
                if (chainId) {
                    return reorderChainByIndex(allItems, chainId);
                }
                return allItems;
            });
            // Select all cubes in the resulting chain (existing + new) so the
            // unified chain overlay shows instead of individual outlines
            var newCubeIds = cubes.map(function (c) { return c.data.id; });
            if (snap && chainId) {
                var existingChainIds = manipulativeRef.current
                    .filter(function (m) { return m.data.type === 'linkingCubes' && m.data.chainId === chainId; })
                    .map(function (m) { return m.data.id; })
                    .filter(function (cid) { return cid !== undefined; });
                // Include standalone target that was just assigned to this new chain
                if (targetUpdateId)
                    existingChainIds.push(targetUpdateId);
                setSelectedTool(Array.from(new Set(__spreadArray(__spreadArray([], existingChainIds, true), newCubeIds, true))));
            }
            else {
                setSelectedTool(newCubeIds);
            }
            setDragState(null);
        };
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        window.addEventListener('touchmove', handleMouseMove, { passive: false });
        window.addEventListener('touchend', handleMouseUp);
        return function () {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
            window.removeEventListener('touchmove', handleMouseMove);
            window.removeEventListener('touchend', handleMouseUp);
            // Safety: clean up temporary blocker if still present
            if (touchScrollBlockerRef.current) {
                window.removeEventListener('touchmove', touchScrollBlockerRef.current);
                touchScrollBlockerRef.current = null;
            }
        };
    }, [dragState === null || dragState === void 0 ? void 0 : dragState.active, zoomLevel, setManipulative, setSelectedTool]);
    // Compute link/unlink disabled states separately
    var _d = (function () {
        var selectedCubes = manipulative.filter(function (m) { return m.type === 'linkingCubes' && m.data.id !== undefined && selectedTool.includes(m.data.id); });
        if (selectedCubes.length < 2)
            return { linkDisabled: true, unlinkDisabled: true };
        var cubeData = selectedCubes.map(function (m) { return m.data; });
        var orientations = new Set(cubeData.map(function (c) { return c.orientation; }));
        if (orientations.size > 1)
            return { linkDisabled: true, unlinkDisabled: true };
        // All selected share same chain? → already linked, disable Link
        var chainIds = new Set(cubeData.map(function (c) { return c.chainId; }).filter(Boolean));
        var allSameChain = chainIds.size === 1 && cubeData.every(function (c) { return c.chainId; });
        // Any selected cube has no chain? → has standalone cubes, disable Unlink
        var hasStandalone = cubeData.some(function (c) { return !c.chainId; });
        return {
            linkDisabled: allSameChain,
            unlinkDisabled: hasStandalone,
        };
    })(), linkDisabled = _d.linkDisabled, unlinkDisabled = _d.unlinkDisabled;
    var handleLink = function () {
        var selectedCount = manipulative.filter(function (m) { return m.type === 'linkingCubes' && m.data.id && selectedTool.includes(m.data.id); }).length;
        if (linkSelectedCubes(selectedTool, manipulative, setManipulative)) {
            playAudio('cubeLink');
            announce(t('text.linkingCubesLinked', { count: selectedCount }));
        }
    };
    var handleUnlink = function () {
        var selectedCount = manipulative.filter(function (m) { return m.type === 'linkingCubes' && m.data.id && selectedTool.includes(m.data.id); }).length;
        if (unlinkSelectedCubes(selectedTool, manipulative, setManipulative)) {
            playAudio('cubeUnlink');
            announce(t('text.linkingCubesUnlinked', { count: selectedCount }));
        }
    };
    // Keyboard-accessible placement: create a cube (or chain of countMode cubes)
    // at the center of the visible canvas. Satisfies WCAG 2.5.7 (Dragging Movements)
    // and 2.1.1 (Keyboard) — no drag required.
    var handleCubePlace = function (colorKey, orientation) { return function () {
        var count = countMode || 1;
        var color = ColorEnum[colorKey];
        var center = getVisibleMidpoint(zoomLevel || 1);
        var chainId = count > 1 ? "chain_".concat(uuidv4().replace(/-/g, '')) : undefined;
        var scaledSize = CUBE_SIZE;
        // Lay cubes out around the canvas center so the chain is centered, not anchored.
        var totalWidth = (count - 1) * scaledSize;
        var startCenter = orientation === 'horizontal'
            ? { x: center.x - totalWidth / 2, y: center.y }
            : { x: center.x, y: center.y + totalWidth / 2 };
        var cubes = [];
        for (var i = 0; i < count; i++) {
            var cubeId = "linkingCube_".concat(uuidv4().replace(/-/g, ''));
            var cubeCenter = orientation === 'horizontal'
                ? { x: startCenter.x + i * scaledSize, y: startCenter.y }
                : { x: startCenter.x, y: startCenter.y - i * scaledSize };
            cubes.push({
                type: 'linkingCubes',
                data: __assign({ id: cubeId, type: 'linkingCubes', center: cubeCenter, position: cubeCenter, color: color, orientation: orientation, scale: 1, rotation: 0, angle: 0, lock: { positioning: false, editing: false } }, (chainId ? { chainId: chainId, chainIndex: i } : {})),
            });
        }
        if (cubes.length > 1) {
            saveState({
                type: 'multipleManipulatives',
                id: cubes[0].data.id,
                propertyName: 'batchCreate',
                old: JSON.stringify(cubes),
            });
        }
        else {
            saveState({
                type: cubes[0].data.id,
                id: cubes[0].data.id.split('_')[1],
                propertyName: 'create',
                old: JSON.stringify(cubes[0]),
            });
        }
        setManipulative(function (prev) { return __spreadArray(__spreadArray([], prev, true), cubes, true); });
        setSelectedTool([cubes[0].data.id]);
        if (chainId)
            playAudio('cubeLink');
        // After React renders the new cube(s), move focus to the base cube so the
        // keyboard/SR user lands on the manipulative they just created.
        var focusTargetId = cubes[0].data.id;
        var tryFocus = function (attempts) {
            if (attempts === void 0) { attempts = 0; }
            var el = document.querySelector("[data-id=\"".concat(focusTargetId, "\"]"));
            if (el) {
                el.focus();
            }
            else if (attempts < 10) {
                setTimeout(function () { return tryFocus(attempts + 1); }, 50);
            }
        };
        requestAnimationFrame(function () { return tryFocus(); });
        var colorLabel = t("text.color".concat(colorKey.charAt(0) + colorKey.slice(1).toLowerCase()));
        var orientationLabel = t("text.".concat(orientation));
        if (count > 1) {
            announce(t('text.linkingCubesChainPlaced', {
                count: count,
                color: colorLabel,
            }));
        }
        else {
            announce(t('text.linkingCubePlaced', {
                color: colorLabel,
                orientation: orientationLabel,
            }));
        }
    }; };
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "linkingCubesToolbar", role: "toolbar", "aria-label": t('text.linkingCubes'), children: [_jsxs("div", { className: "lcActionBtns", role: "group", "aria-label": t('text.linkingCubesGroupSize'), children: [_jsx(ButtonComponent, { text: t("text.five"), defaultCSS: "lcCountBtn", normalCSS: "lcCountBtn", clickActive: true, isActive: countMode === 5, ariaPressed: countMode === 5, clickHandler: function () {
                                    var next = countMode === 5 ? null : 5;
                                    setCountMode(next);
                                    announce(next === null
                                        ? t('text.linkingCubesGroupSizeOff')
                                        : t('text.linkingCubesGroupSizeSet', { count: next }));
                                }, direction: "left", showToolTip: true, title: t("text.grp5"), ariaLabel: "".concat(t('text.grp5'), ", ").concat(countMode === 5
                                    ? t('text.linkingCubesCurrentlySelected')
                                    : t('text.linkingCubesNotSelected')) }), _jsx(ButtonComponent, { text: t("text.ten"), defaultCSS: "lcCountBtn", normalCSS: "lcCountBtn", clickActive: true, isActive: countMode === 10, ariaPressed: countMode === 10, clickHandler: function () {
                                    var next = countMode === 10 ? null : 10;
                                    setCountMode(next);
                                    announce(next === null
                                        ? t('text.linkingCubesGroupSizeOff')
                                        : t('text.linkingCubesGroupSizeSet', { count: next }));
                                }, direction: "left", showToolTip: true, title: t("text.grp10"), ariaLabel: "".concat(t('text.grp10'), ", ").concat(countMode === 10
                                    ? t('text.linkingCubesCurrentlySelected')
                                    : t('text.linkingCubesNotSelected')) })] }), _jsx(CustomScrollbar, { direction: "vertical", className: "cubeColorGridWrapper", children: _jsx("div", { className: "cubeColorGrid", role: "list", "aria-label": t('text.cubeColorsLabel'), children: CUBE_COLORS.map(function (colorKey) {
                                var colorName = getColorName(colorKey);
                                return (_jsxs(Fragment, { children: [_jsx(ButtonComponent, { imgSrc: getCubeImgSrc('vertical', colorKey), defaultCSS: "cubePreview", normalCSS: "cubePreview", avoidDefaultStyle: true, noAudio: true, onMouseDown: handleCubeDragStart(colorKey, 'vertical'), onTouchStart: handleCubeDragStart(colorKey, 'vertical'), clickHandler: function () { return placeCubeAtCanvas(colorKey, 'vertical'); }, ariaLabelTranslated: t('text.cubePreviewLabel', {
                                                color: colorName,
                                                orientation: t('text.cubeOrientationVertical'),
                                            }), role: "listitem" }), _jsx(ButtonComponent, { imgSrc: getCubeImgSrc('horizontal', colorKey), defaultCSS: "cubePreview horizontalCube", normalCSS: "cubePreview", avoidDefaultStyle: true, noAudio: true, onMouseDown: handleCubeDragStart(colorKey, 'horizontal'), onTouchStart: handleCubeDragStart(colorKey, 'horizontal'), clickHandler: function () { return placeCubeAtCanvas(colorKey, 'horizontal'); }, ariaLabelTranslated: t('text.cubePreviewLabel', {
                                                color: colorName,
                                                orientation: t('text.cubeOrientationHorizontal'),
                                            }), role: "listitem" })] }, colorKey));
                            }) }) }), _jsxs("div", { className: "lcActionBtns", role: "group", "aria-label": t('text.linkingCubesLinkActions'), children: [_jsx(ButtonComponent, { imgSrc: link.imgSrc, defaultCSS: "lcBtn ".concat(linkDisabled ? 'disableButton' : ''), normalCSS: "lcBtn", clickHandler: handleLink, disabled: linkDisabled, title: t("".concat(link.text)), showToolTip: true, direction: "left", avoidDefaultStyle: true, noAudio: true, ariaLabel: t("".concat(link.text)) }), _jsx(ButtonComponent, { imgSrc: unLink.imgSrc, defaultCSS: "lcBtn ".concat(unlinkDisabled ? 'disableButton' : ''), normalCSS: "lcBtn", clickHandler: handleUnlink, disabled: unlinkDisabled, title: t("".concat(unLink.text)), showToolTip: true, direction: "left", avoidDefaultStyle: true, noAudio: true, ariaLabel: t("".concat(unLink.text)) })] })] }), (dragState === null || dragState === void 0 ? void 0 : dragState.active) &&
                createPortal(_jsx("div", { className: "linkingCubeGhost", "aria-hidden": "true", style: {
                        left: dragState.pos.x - CUBE_SIZE / 2,
                        top: dragState.orientation === 'vertical'
                            ? dragState.pos.y - (dragState.count - 1) * CUBE_SIZE - CUBE_SIZE / 2
                            : dragState.pos.y - CUBE_SIZE / 2,
                        width: dragState.orientation === 'horizontal'
                            ? (dragState.count - 1) * CUBE_SIZE + CSS_CUBE_SIZE
                            : CSS_CUBE_SIZE,
                        height: dragState.orientation === 'vertical'
                            ? (dragState.count - 1) * CUBE_SIZE + CSS_CUBE_SIZE
                            : CSS_CUBE_SIZE,
                    }, children: Array.from({ length: dragState.count }).map(function (_, i) { return (_jsx("img", { src: getCubeImgSrc(dragState.orientation, dragState.colorKey).normal, alt: "", draggable: false, className: "ghostCube", style: {
                            position: 'absolute',
                            width: CSS_CUBE_SIZE,
                            height: CSS_CUBE_SIZE,
                            left: dragState.orientation === 'horizontal' ? i * CUBE_SIZE : 0,
                            bottom: dragState.orientation === 'vertical' ? i * CUBE_SIZE : 0,
                        } }, i)); }) }), document.body)] }));
};
export default LinkingCubesToolbar;
